import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from 'sonner';
import { api } from '@/contexts/AuthContext';
import { useDefaultCountryCode } from '@/hooks/useDefaultCountryCode';
import { ArrowLeft, ArrowRight, Check, AlertTriangle } from "lucide-react";
import type {
  CampaignData,
  GoogleSheetInfo,
  CampaignSheetData,
  MessagePreview,
  TemplateVariable,
  CampaignStep,
  CampaignListItem, // ✅ MANTENER
} from '../types';

// --- ADD: markdown renderer imports
import { Twemoji } from 'react-emoji-render';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import CreateTemplateForm from "./CreateTemplateForm";
import type { NewTemplateData } from "@/types";
import MessageTemplates from './MessageTemplates';
import type { MessageTemplate } from '@/types';
import TemplateColumnMapper from './TemplateColumnMapper';
import InlineSheetsSelector from './InlineSheetsSelector';
import type { SheetDataLoadResponse } from '@/types';
import { PlanStatusAlert } from './PlanStatusAlert';
import { usePlanStatus } from '@/hooks/usePlanStatus';

// ✅ axios ya está configurado globalmente en AuthContext.tsx
// No necesitamos redefinir baseURL aquí

// ✅ NUEVA DEFINICIÓN (reemplaza la importada)
interface CampaignBuilderProps {
  onBack?: () => void;
  editingCampaign?: CampaignListItem | null;
}

const CampaignBuilder: React.FC<CampaignBuilderProps> = ({
  onBack,
  editingCampaign
}) => {
  const [step, setStep] = useState<number>(1);
  const { isBlocked, getBlockedMessage } = usePlanStatus();
  const { defaultCountryCode } = useDefaultCountryCode();

  // ✅ AGREGAR: Función para abrir el modal de crear plantilla
  const goToCreateTemplate = () => {
    setShowCreateTemplateModal(true);
  };

  // ✅ NUEVO: Función para cargar datos del caché
  const loadCachedData = (): CampaignData => {
    try {
      const cached = localStorage.getItem('campaignBuilder_draft');
      if (cached) {
        const parsedData = JSON.parse(cached);
        console.log('📦 Datos cargados del caché:', parsedData);
        return {
          name: parsedData.name || '',
          google_sheet_id: parsedData.google_sheet_id || '',
          message_template: parsedData.message_template || '',
          phone_column: parsedData.phone_column || '',
          country_code: parsedData.country_code || defaultCountryCode,
          ...parsedData
        };
      }
    } catch (error) {
      console.warn('Error cargando datos del caché:', error);
    }

    return {
      name: '',
      google_sheet_id: '',
      message_template: '',
      phone_column: '',
      country_code: defaultCountryCode
    };
  };

  const [campaignData, setCampaignData] = useState<CampaignData>(loadCachedData);
  const [sheets, setSheets] = useState<GoogleSheetInfo[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null);
  const [selectedSheetTab, setSelectedSheetTab] = useState<string>("");
  const [selectedSheetTabId, setSelectedSheetTabId] = useState<number | null>(null);
  const [sheetData, setSheetData] = useState<CampaignSheetData | null>(null);
  const [messagePreview, setMessagePreview] = useState<MessagePreview[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState("");
  // guardamos también el contenido de la plantilla para mostrar preview en el resumen
  const [templates, setTemplates] = useState<{ id: string; name: string; template?: string; header_image_url?: string; meta_template_name?: string }[]>([]);
  // Meta WhatsApp templates (approved by Meta)
  const [metaTemplates, setMetaTemplates] = useState<{
    name: string;
    language: string;
    status: string;
    category: string;
    body?: string;
    components?: any[];
    buttons?: any[];
    header_image_url?: string;
    header_type?: string;
  }[]>([]);
  const [loadingMetaTemplates, setLoadingMetaTemplates] = useState<boolean>(false);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState<boolean>(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState<boolean>(false);
  // Pagination for "Seleccionar Datos"
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 6;
  // controlar si la barra lateral del modal está visible (colapsable)
  const [modalSidebarOpen, setModalSidebarOpen] = useState<boolean>(true);
  // Column mapping for template variables
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});

  // Estado para controlar si hay hojas conectadas
  const [hasConnectedSheets, setHasConnectedSheets] = useState<boolean>(false);

  // Nuevo: estado reutilizable para crear plantilla desde modal (reusar CreateTemplateForm)
  const [newTemplate, setNewTemplate] = useState<NewTemplateData>({
    name: "",
    template: "",
    category: "UTILITY",
    language: "es",
    // cualquier otro campo que NewTemplateData requiera puede ir aquí
  });

  // ✅ NUEVO: Función para guardar datos en caché
  const saveToCacheDebounced = React.useCallback(
    debounce((data: CampaignData) => {
      try {
        // Solo guardar si hay datos significativos
        if (data.name || data.google_sheet_id || (data as any).templateType) {
          localStorage.setItem('campaignBuilder_draft', JSON.stringify(data));
          console.log('💾 Datos guardados en caché:', data);
        }
      } catch (error) {
        console.warn('Error guardando en caché:', error);
      }
    }, 1000),
    []
  );

  // ✅ NUEVO: Función para limpiar caché
  const clearCache = () => {
    try {
      localStorage.removeItem('campaignBuilder_draft');
      console.log('🗑️ Caché limpiado');
    } catch (error) {
      console.warn('Error limpiando caché:', error);
    }
  };

  // ✅ NUEVO: Guardar en caché cuando cambian los datos
  useEffect(() => {
    if (!editingCampaign) { // Solo usar caché para campañas nuevas
      saveToCacheDebounced(campaignData);
    }
  }, [campaignData, editingCampaign, saveToCacheDebounced]);

  // ✅ NUEVO: Detectar si venimos de Google Sheets y restaurar el paso correcto
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromGoogleSheets = params.get('from') === 'google-sheets';
    const cachedStep = params.get('step');

    if (fromGoogleSheets && !editingCampaign) {
      console.log('🔄 Regresando de Google Sheets, restaurando contexto...');

      // Restaurar el paso si está especificado
      if (cachedStep) {
        const stepNumber = parseInt(cachedStep, 10);
        if (stepNumber >= 1 && stepNumber <= 4) {
          setStep(stepNumber);
        }
      }

      // Limpiar los parámetros de la URL
      params.delete('from');
      params.delete('step');
      const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
      window.history.replaceState({}, '', newUrl);

      // Recargar hojas para mostrar las recién conectadas
      setTimeout(() => {
        loadUserSheets();
      }, 500);
    }
  }, [editingCampaign]);

  // ✅ NUEVO: Función helper para debounce
  function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
    let timeout: NodeJS.Timeout;
    return ((...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(null, args), wait);
    }) as T;
  }

  // ✅ NUEVA FUNCIÓN: Convierte formato WhatsApp a Markdown
  const whatsappToMarkdown = (text: string): string => {
    // Negrita: *texto* → **texto**
    text = text.replace(/\*([^*]+)\*/g, '**$1**');

    // Tachado: ~texto~ → ~~texto~~
    text = text.replace(/~([^~]+)~/g, '~~$1~~');

    // Cursiva: _texto_ (ya es compatible con Markdown)
    return text;
  };

  // ✅ Helper simplificado: axios.defaults.baseURL ya está configurado correctamente
  const apiPath = (path: string) => {
    // Si la ruta ya es relativa y no empieza con /api, úsala tal cual
    // (axios.defaults.baseURL = "https://.../api" automáticamente la completa)
    return path.startsWith('/api') ? path.slice(4) : path;
  };

  // handleCreateTemplate: recibe payload desde el formulario, crea plantilla y la selecciona
  const handleCreateTemplate = async (payloadFromForm?: NewTemplateData) => {
    // If _skipInsert is true, the template was already created by /whatsapp/templates/create
    // We just need to refresh templates and close the modal
    if ((payloadFromForm as any)?._skipInsert) {
      await loadTemplates();
      const newId = (payloadFromForm as any)?.id;
      if (newId) {
        setCampaignData((prev) => ({
          ...prev,
          template_id: String(newId),
          templateType: String(newId),
        } as any));
      }
      setShowCreateTemplateModal(false);
      toast.success("Plantilla creada exitosamente");
      return;
    }

    try {
      const payload: any = {
        name: (payloadFromForm?.name ?? newTemplate.name ?? "Sin nombre").trim(),
        template: (payloadFromForm?.template ?? newTemplate.template ?? "").trim(),
        category: payloadFromForm?.category ?? newTemplate.category ?? "General",
        variables: payloadFromForm?.variables ?? (newTemplate as any)?.variables ?? [],
        language: payloadFromForm?.language ?? newTemplate.language ?? "es",
        used_count: payloadFromForm?.used_count ?? (newTemplate as any)?.used_count ?? 0,
        // ✅ Capture header_image_url for persistence in templates collection
        header_image_url: payloadFromForm?.header_image_url ?? (newTemplate as any)?.header_image_url ?? undefined,
        // no es necesario enviar created_at/updated_at — el backend debe asignarlos,
        // pero si el formulario envía updated_at lo incluimos como fallback
        ...(payloadFromForm?.updated_at ? { updated_at: payloadFromForm.updated_at } : {})
      };

      console.debug("Creating template payload:", payload);
      const res = await api.post('/templates', payload); // ✅ Ruta relativa simple
      const created = res.data;
      await loadTemplates();
      const newId = created?.id ?? created?._id ?? String(created);
      if (newId) {
        setCampaignData((prev) => ({
          ...prev,
          template_id: String(newId),
          templateType: String(newId),
          template_name: created?.name ?? prev.template_name,
          message_template: created?.template ?? prev.message_template,
        } as any));
      }
      toast.success("Plantilla creada y seleccionada");
      setNewTemplate({ name: "", template: "", category: "UTILITY", language: "es" });
    } catch (err) {
      console.error("Error creando plantilla:", err);
      toast.error("No se pudo crear la plantilla");
      throw err;
    } finally {
      setShowCreateTemplateModal(false);
    }
  };

  // nuevo handler: cuando el usuario selecciona una plantilla desde MessageTemplates
  const handleSelectTemplate = async (tpl: MessageTemplate) => {
    const tplId = (tpl as any).id ?? (tpl as any)._id ?? String(tpl);
    // actualizar estado local inmediatamente
    setCampaignData(prev => ({
      ...prev,
      template_id: tplId,
      templateType: tplId,
      template_name: tpl.name ?? prev.template_name,
      message_template: tpl.template ?? prev.message_template,
      template_variables: tpl.variables ?? prev.template_variables
    } as any));

    toast.success('Plantilla seleccionada');

    // si la campaña ya existe en el backend, persistir la asociación inmediatamente
    const campaignId = (campaignData as any)?.id;
    if (campaignId) {
      try {
        await api.patch(apiPath(`/campaigns/${campaignId}`), { template_id: tplId }, { withCredentials: true });
        toast.success('Asociación plantilla ↔ campaña guardada');
      } catch (err) {
        console.error('Error guardando template_id en campaña:', err);
        toast.error('No se pudo guardar la plantilla en el servidor');
      }
    }
  };

  useEffect(() => {
    console.log('🚀 CampaignBuilder montado - Iniciando carga de datos...');
    loadUserSheets();
    loadTemplates();
    loadMetaTemplates(); // Load Meta WhatsApp approved templates

    // ✅ Si venimos de un draft y tenemos hoja seleccionada, cargar su pestaña exacta
    if (campaignData.google_sheet_id && !editingCampaign) {
      const cachedTabName =
        (campaignData as any).google_sheet_tab_name || "";

      const cachedTabId =
        (campaignData as any).google_sheet_tab_id ?? null;

      setSelectedSheet(campaignData.google_sheet_id);
      setSelectedSheetTab(cachedTabName);
      setSelectedSheetTabId(cachedTabId);

      loadSheetData(
        campaignData.google_sheet_id,
        cachedTabName || undefined,
        cachedTabId
      );
    }
  }, []); // eslint-disable-line

  // Si venimos de la página de crear plantilla, seleccionamos la plantilla recién creada automáticamente
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const newTplId = params.get('newTemplateId') || params.get('createdTemplateId') || params.get('templateId');
    if (!newTplId) return;

    (async () => {
      try {
        // recargar plantillas para asegurar que esté la nueva
        await loadTemplates();
        setCampaignData(prev => ({ ...prev, templateType: newTplId } as any));
        toast.success('Plantilla creada y seleccionada');

        // eliminar el query param para evitar que se vuelva a procesar al recargar
        params.delete('newTemplateId');
        params.delete('createdTemplateId');
        params.delete('templateId');
        const search = params.toString();
        const newUrl = window.location.pathname + (search ? `?${search}` : '');
        window.history.replaceState({}, '', newUrl);
      } catch (e) {
        console.debug('No se pudo seleccionar la plantilla automáticamente', e);
      }
    })();
  }, []);

  // ✅ NUEVO: Inicializar datos al editar
  useEffect(() => {
    if (editingCampaign) {
      const anyc = editingCampaign as any;

      // Parsear send_at para obtener fecha y hora
      let sendDate: Date | string | undefined;
      let sendTime: string | undefined;

      const sendAtRaw = anyc.send_at ?? anyc.sendAt ?? anyc.scheduled_at;
      if (sendAtRaw) {
        try {
          const date = new Date(sendAtRaw);
          if (!isNaN(date.getTime())) {
            sendDate = date;
            sendTime = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
          }
        } catch (e) {
          console.warn('No se pudo parsear send_at:', e);
        }
      }

      const existingTabName =
        anyc.google_sheet_tab_name ??
        anyc.googleSheetTabName ??
        "";

      const existingTabId =
        anyc.google_sheet_tab_id ??
        anyc.googleSheetTabId ??
        null;

      setCampaignData({
        name: anyc.name || '',
        google_sheet_id: anyc.google_sheet_id ?? anyc.googleSheetId ?? '',
        google_sheet_tab_id: existingTabId,
        google_sheet_tab_name: existingTabName,
        message_template: anyc.message_template ?? anyc.message ?? '',
        phone_column: anyc.phone_column ?? anyc.phoneColumn ?? '',
        country_code: anyc.country_code ?? anyc.countryCode ?? defaultCountryCode,
        template_id: anyc.template_id ?? anyc.templateId ?? null,
        templateType: anyc.template_id ?? anyc.templateId ?? null,
        template_name: anyc.template_name ?? '',
        sendDate,
        sendTime,
        // WhatsApp Meta template fields
        whatsapp_template_name: anyc.whatsapp_template_name ?? '',
        whatsapp_template_language: anyc.whatsapp_template_language ?? 'es_MX',
      } as any);

      // Seleccionar la hoja de Google y restaurar su pestaña
      if (anyc.google_sheet_id) {
        setSelectedSheet(anyc.google_sheet_id);
        setSelectedSheetTab(existingTabName);
        setSelectedSheetTabId(existingTabId);

        loadSheetData(
          anyc.google_sheet_id,
          existingTabName || undefined,
          existingTabId
        );
      }

      console.log('📝 Campaña cargada para edición:', anyc);
    }
  }, [editingCampaign]);

  // Regenerar preview automáticamente cuando se entra en la pantalla "Vista Previa"
  useEffect(() => {
    if (step !== 4) return;
    // Re-generate preview when entering preview step or when source data changes
    (async () => {
      try {
        await generatePreview();
      } catch (err) {
        console.debug('generatePreview failed:', err);
      }
    })();
    // Dependencias: step + data that affects preview
  }, [step, sheetData, campaignData.message_template, (campaignData as any).phone_column]);

  const loadUserSheets = async (): Promise<void> => {
    try {
      const response = await api.get('/google-sheets'); // ✅ Ya es correcta

      // Sort sheets by last_modified date (most recent first) as additional safety
      const sortedSheets = response.data.sort((a, b) => {
        const dateA = new Date(a.last_modified || a.updated_at || a.created_at);
        const dateB = new Date(b.last_modified || b.updated_at || b.created_at);
        return dateB.getTime() - dateA.getTime(); // Descending order (most recent first)
      });

      setSheets(sortedSheets);
      setHasConnectedSheets(sortedSheets.length > 0);
      console.log('📋 Sheets loaded and sorted by modification date:', sortedSheets);
    } catch (error: any) {
      console.error('Error loading sheets:', error, error?.response?.status, error?.response?.data);

      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a Google Sheets', {
          description: 'Verifica que tu cuenta tenga los permisos necesarios'
        });

        // Redirigir a página de configuración con contexto
        setTimeout(() => {
          window.location.href = '/google-sheets?context=campaign-builder';
        }, 2000);

        return;
      }

      toast.error('Error al cargar las hojas de Google Sheets');
      setHasConnectedSheets(false);
    }
  };

  const handleSheetSelected = (
    sheetId: string,
    sheetDataResponse?: SheetDataLoadResponse,
    tabName?: string,
    tabId?: number | null
  ): void => {
    setSelectedSheet(sheetId);
    setSelectedSheetTab(tabName || "");
    setSelectedSheetTabId(tabId ?? null);

    setCampaignData(prev => ({
      ...prev,
      google_sheet_id: sheetId,
      google_sheet_tab_id: tabId ?? null,
      google_sheet_tab_name: tabName || ""
    } as any));

    if (sheetDataResponse) {
      // Convert array rows to object rows for compatibility
      const convertedRows: Record<string, string>[] = [];
      if (sheetDataResponse.rows && sheetDataResponse.columns) {
        sheetDataResponse.rows.forEach(row => {
          if (Array.isArray(row)) {
            // Convert array row to object using columns as keys
            const rowObj: Record<string, string> = {};
            sheetDataResponse.columns.forEach((col, index) => {
              rowObj[col] = row[index]?.toString() || '';
            });
            convertedRows.push(rowObj);
          } else if (typeof row === 'object' && row !== null) {
            // Already an object, just ensure string values
            const rowObj: Record<string, string> = {};
            Object.entries(row).forEach(([key, value]) => {
              rowObj[key] = value?.toString() || '';
            });
            convertedRows.push(rowObj);
          }
        });
      }

      setSheetData({
        columns: sheetDataResponse.columns || [],
        rows: convertedRows,
        sheet_name: sheetDataResponse.sheet_name || '',
        total_rows_fetched: sheetDataResponse.total_rows_fetched || 0
      });
    }

    // Actualizar el estado de hojas conectadas
    setHasConnectedSheets(true);

    toast.success('Hoja seleccionada correctamente');
  };

  const loadTemplates = async (): Promise<void> => {
    try {
      const response = await api.get('/templates');
      setTemplates(response.data.sort((a: any, b: any) => {
        // Ordenar por nombre (alfabéticamente)
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      }));
    } catch (error: any) {
      console.error('Error loading templates:', error);

      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a plantillas', {
          description: 'Contacta al administrador del sistema'
        });
        setTemplates([]);
        return;
      }

      toast.error('Error al cargar plantillas');
      setTemplates([]);
    }
  };

  // Load Meta WhatsApp templates (approved templates from Meta)
  const loadMetaTemplates = async (): Promise<void> => {
    console.log('🔄 Iniciando carga de plantillas Meta WhatsApp...');
    setLoadingMetaTemplates(true);
    try {
      console.log('📡 Llamando a /whatsapp/templates/meta/list...');
      const response = await api.get('/whatsapp/templates/meta/list');
      console.log('📥 Respuesta recibida:', response.data);

      // La respuesta tiene estructura { success: true, templates: [...] }
      if (response.data.success && Array.isArray(response.data.templates)) {
        console.log(`📋 Total de plantillas recibidas: ${response.data.templates.length}`);
        
        const approvedTemplates = response.data.templates
          .filter((t: any) => t.status === 'APPROVED')
          .map((t: any) => {
            // Extraer el texto del BODY de los components
            const bodyComponent = t.components?.find((c: any) => c.type === 'BODY');
            let bodyText = bodyComponent?.text || '';

            // ✅ Extraer footer y buttons
            const footerComponent = t.components?.find((c: any) => c.type === 'FOOTER');
            const buttonsComponent = t.components?.find((c: any) => c.type === 'BUTTONS');
            const buttons = buttonsComponent?.buttons?.map((btn: any) => ({
              type: btn.type, text: btn.text, url: btn.url, phone_number: btn.phone_number
            })) || [];

            // 🧹 CLEANUP: Quitar appended strings de Gupshup y Footer
            bodyText = bodyText.replace(/(\s*\|\s*\[.*?\])+$/g, '');
            const fText = footerComponent?.text ? footerComponent.text.trim() : null;
            if (fText) {
              if (bodyText.endsWith(` | ${fText}`)) bodyText = bodyText.slice(0, -(fText.length + 3));
              if (bodyText.endsWith(`|${fText}`)) bodyText = bodyText.slice(0, -(fText.length + 1));
              bodyText = bodyText.trim();
            }

            // ✅ Extraer header: tipo e imagen
            const headerComponent = t.components?.find((c: any) => c.type === 'HEADER');
            const headerType = headerComponent?.format || null; // 'TEXT' | 'IMAGE' | etc.
            let headerImageUrl: string | null = null;
            if (headerType === 'IMAGE') {
              headerImageUrl =
                headerComponent.example?.header_handle?.[0] ||
                headerComponent.example?.header_url?.[0] ||
                null;
            }
            const headerText = headerType === 'TEXT' ? (headerComponent?.text || null) : null;

            return {
              name: t.name,
              language: t.language || 'es_MX',
              status: t.status,
              category: t.category || 'UTILITY',
              body: bodyText,
              components: t.components || [],
              // ✅ Nuevos campos
              header_type: headerType,
              header_image_url: headerImageUrl,
              header_text: headerText,
              footer_text: footerComponent?.text || null,
              buttons
            };
          });
        setMetaTemplates(approvedTemplates);
        console.log(`✅ Plantillas APROBADAS cargadas: ${approvedTemplates.length}`, approvedTemplates);
      } else {
        console.warn('⚠️ Respuesta inesperada de Meta templates:', response.data);
        setMetaTemplates([]);
      }
    } catch (error: any) {
      console.error('❌ Error loading Meta templates:', error);
      console.error('📄 Error response:', error?.response?.data);
      console.error('📄 Error status:', error?.response?.status);
      // Don't show error toast - Meta templates are optional
      setMetaTemplates([]);
    } finally {
      setLoadingMetaTemplates(false);
      console.log('✅ Carga de plantillas Meta finalizada');
    }
  };

  const loadSheetData = async (
    sheetId: string,
    tabName?: string,
    tabId?: number | null
  ): Promise<void> => {
    try {
      setSelectedSheet(sheetId);

      if (tabName) {
        setSelectedSheetTab(tabName);
      }

      setSheetData(null);
      console.log('🔍 Loading sheet data for sheet_id:', sheetId);
      console.log('📋 Loading tab:', tabName || 'default');

      const response = await api.get(`/google-sheets/${sheetId}/data`, {
        params: {
          ...(tabName ? { sheet_name: tabName } : {}),
          ...(tabId != null ? { google_sheet_tab_id: tabId } : {}),
        },
      });

      setSheetData(response.data);

      // asegurar que la campaña referencia la hoja seleccionada
      setCampaignData(prev => ({ ...prev, google_sheet_id: sheetId }));

      console.log('✅ Sheet data loaded successfully:', {
        columns: response.data.columns?.length ?? 0,
        rows: response.data.rows?.length ?? 0
      });
    } catch (error: any) {
      console.error('❌ Error loading sheet data:', error);
      console.error('📄 Response data:', error?.response?.data);

      // ✅ Manejar error 401 (token expirado)
      if (error?.response?.status === 401) {
        const detail = error?.response?.data?.detail || '';

        if (detail.includes('AUTH_EXPIRED')) {
          toast.error('🔄 Tu sesión de Google expiró. Redirigiendo para reconectar...', {
            duration: 3000,
            icon: '⏰'
          });

          // Redirigir a la página de Google Sheets después de 2 segundos
          setTimeout(() => {
            window.location.href = '/google-sheets?context=campaign-builder';
          }, 2000);

          return;
        }

        if (detail.includes('AUTH_REQUIRED')) {
          toast.error('⚠️ No tienes autenticación de Google. Redirigiendo...', {
            duration: 3000
          });

          setTimeout(() => {
            window.location.href = '/google-sheets?context=campaign-builder';
          }, 2000);

          return;
        }

        // Otros errores 401
        toast.error('🔒 Error de autenticación. Por favor reconecta tu cuenta de Google.');
        setTimeout(() => {
          window.location.href = '/google-sheets?context=campaign-builder';
        }, 2000);

        return;
      }

      // ✅ Manejar otros errores (400, 500, etc)
      const backendMsg = error?.response?.data?.detail ?? 'Error desconocido';
      toast.error(`❌ Error al cargar los datos de la hoja: ${backendMsg}`);
    }
  };

  // Asegura que la hoja esté recargada antes de mostrar la Vista Previa
  const handleGoToPreview = async () => {
    try {
      const sheetToLoad = selectedSheet || campaignData.google_sheet_id || null;

      const tabToLoad =
        selectedSheetTab ||
        (campaignData as any).google_sheet_tab_name ||
        undefined;

      const tabIdToLoad =
        selectedSheetTabId ??
        (campaignData as any).google_sheet_tab_id ??
        null;

      if (sheetToLoad && !sheetData) {
        console.debug(
          'Preview: loading sheet before showing preview',
          sheetToLoad,
          tabToLoad,
          tabIdToLoad
        );

        await loadSheetData(
          sheetToLoad,
          tabToLoad,
          tabIdToLoad
        );
      } else if (sheetToLoad) {
        // Siempre intentar refrescar los datos para reflejar cambios externos
        await loadSheetData(
          sheetToLoad,
          tabToLoad,
          tabIdToLoad
        );
      }
    } catch (e) {
      console.debug('Error loading sheet before preview:', e);
    } finally {
      setStep(4);
    }
  };

  const generatePreview = async (): Promise<void> => {
    if (!campaignData.message_template || !sheetData) return;

    const variables = extractVariablesFromTemplate(campaignData.message_template);

    // Si ya existe una campaign (id) se podría pedir al backend; para MVP usamos preview local
    try {
      const campaignId = (campaignData as any).id as string | undefined;
      if (campaignId) {
        const response = await api.post<{ previews: MessagePreview[] }>(
          `/campaigns/${campaignId}/preview`,
          { template: { template: campaignData.message_template, variables } }
        );
        setMessagePreview(response.data.previews || []);
      } else {
        const localPreview = generateLocalPreview();
        setMessagePreview(localPreview);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      const localPreview = generateLocalPreview();
      setMessagePreview(localPreview);
    }
  };

  const extractVariablesFromTemplate = (template: string): TemplateVariable[] => {
    const regex = /\{\{([^}]+)\}\}/g;
    const variables: TemplateVariable[] = [];
    let match: RegExpExecArray | null;

    while ((match = regex.exec(template)) !== null) {
      const varName = match[1].trim();
      if (variables.find(v => v.name === varName)) continue;

      variables.push({
        name: varName,
        column: varName // Default mapping
      });
    }

    return variables;
  };

  const generateLocalPreview = (): MessagePreview[] => {
    if (!sheetData || !campaignData.message_template) return [];

    return sheetData.rows.slice(0, 3).map(row => {
      let message = campaignData.message_template;

      // Replace variables with actual data
      Object.keys(row).forEach(column => {
        const placeholder = `{{${column}}}`;
        message = message.replace(new RegExp(placeholder, 'g'), row[column] || '');
      });

      return {
        phone_number: row['WhatsApp'] || row['Telefono'] || 'N/A',
        message: message,
        row_data: row
      };
    });
  };

  const saveCampaign = async (): Promise<void> => {
    setSaving(true);
    try {
      // ✅ Construir send_at con offset de Colombia
      let sendAt: string | null = null;

      if (campaignData.sendDate && campaignData.sendTime) {
        try {
          let dateObj: Date;

          if (campaignData.sendDate instanceof Date) {
            dateObj = new Date(campaignData.sendDate);
          } else if (typeof campaignData.sendDate === 'string') {
            const [year, month, day] = campaignData.sendDate.split('-').map(Number);
            dateObj = new Date(year, month - 1, day);
          } else {
            dateObj = new Date(campaignData.sendDate);
          }

          const [hours, minutes] = campaignData.sendTime.split(':').map(Number);

          if (!isNaN(dateObj.getTime()) && !isNaN(hours) && !isNaN(minutes)) {
            const colombiaDateString = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00-05:00`;
            sendAt = colombiaDateString;

            console.log('✅ send_at construido:', {
              fechaInput: campaignData.sendDate,
              horaInput: campaignData.sendTime,
              resultadoISO: sendAt,
            });
          } else {
            console.error('❌ Fecha u hora inválida');
          }
        } catch (e) {
          console.error("❌ Error construyendo send_at:", e);
          toast.error('Error al procesar la fecha y hora de envío');
          setSaving(false);
          return;
        }
      }

      const payload: any = {
        name: campaignData.name.trim(),
        google_sheet_id: campaignData.google_sheet_id || selectedSheet || null,
        google_sheet_tab_id:
          selectedSheetTabId ??
          (campaignData as any).google_sheet_tab_id ??
          undefined,
        google_sheet_tab_name:
          selectedSheetTab ||
          (campaignData as any).google_sheet_tab_name ||
          undefined,
        message_template: campaignData.message_template?.trim() || "",
        template_id: (campaignData as any).template_id ?? (campaignData as any).templateType ?? null,
        phone_column: (campaignData as any).phone_column || null,
        country_code: (campaignData as any).country_code || defaultCountryCode,
        send_at: sendAt,
        estimated_messages: (campaignData as any).estimated_messages ?? sheetData?.rows?.length ?? 0,
        // WhatsApp Meta template fields
        whatsapp_template_name: campaignData.whatsapp_template_name || null,
        whatsapp_template_language: campaignData.whatsapp_template_language || 'es_MX',
        // Column mapping for template variables
        column_mapping: columnMapping || {}
      };

      console.debug("📤 Payload:", payload);

      // ✅ NUEVO: Detectar si estamos editando o creando
      const isEditing = editingCampaign && (editingCampaign as any).id;

      let res;
      if (isEditing) {
        // ✅ MODO EDICIÓN: usar PATCH
        const campaignId = (editingCampaign as any).id;
        console.log('✏️ Actualizando campaña existente:', campaignId);

        res = await api.patch(`/campaigns/${campaignId}`, payload); // ✅ Ruta relativa simple

        toast.success('¡Campaña actualizada exitosamente!');
      } else {
        // ✅ MODO CREACIÓN: usar POST
        console.log('➕ Creando nueva campaña');

        res = await api.post('/campaigns', payload); // ✅ Ruta relativa simple

        toast.success('¡Campaña creada exitosamente!');
      }

      if (res?.data?.id) {
        setCampaignData(prev => ({ ...prev, id: res.data.id } as any));
      }

      console.log('✅ Respuesta del servidor:', res.data);

      // ✅ NUEVO: Limpiar caché después de guardar exitosamente
      if (!editingCampaign) {
        clearCache();
      }

      onBack(); // Cerrar modal y recargar lista

    } catch (error: any) {
      console.error('❌ Error guardando campaña:', error);
      console.error('📄 Response:', error?.response?.data);

      const errorMsg = error?.response?.data?.detail ||
        error?.response?.data?.message ||
        'Error al guardar la campaña';

      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (variable: string): void => {
    const placeholder = `{{${variable}}}`;
    setCampaignData({
      ...campaignData,
      message_template: campaignData.message_template + placeholder
    });
  };

  const formatTime12h = (time: string) => {
    if (!time) return "";
    let [hour, minute] = time.split(":").map(Number);
    const ampm = hour >= 12 ? "p. m." : "a. m.";
    hour = hour % 12 || 12;
    return `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
  };

  // Notar: eliminamos el paso intermedio "Crear Mensaje" (case 3).
  // El flujo será: 1 (Info) -> 2 (Seleccionar Datos) -> 4 (Vista Previa).
  const steps: CampaignStep[] = [
    { number: 1, title: 'Información Básica', icon: '📝' },
    { number: 2, title: 'Seleccionar Datos', icon: '📋' },
    { number: 3, title: 'Mapear Variables', icon: '🔗' },
    { number: 4, title: 'Vista Previa', icon: '👀' }
  ];

  const StepIndicator: React.FC = () => (
    <div className="flex items-center justify-center mb-8">
      {steps.map((stepItem, index) => (
        <div key={stepItem.number} className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${stepItem.number <= step
            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
            : 'bg-gray-200 text-gray-500'
            }`}>
            <span className="text-sm">{stepItem.icon}</span>
          </div>
          <div className="ml-2 mr-4">
            <div className={`text-sm font-medium ${stepItem.number <= step ? 'text-blue-600' : 'text-gray-500'
              }`}>
              {stepItem.title}
            </div>
          </div>
          {index < steps.length - 1 && (
            <div className={`w-8 h-0.5 mx-2 ${stepItem.number < step ? 'bg-blue-600' : 'bg-gray-200'
              }`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep = (): React.ReactElement | null => {
    // Log del estado actual
    if (step === 1) {
      console.log('📊 Estado del paso 1:', {
        loadingMetaTemplates,
        metaTemplatesCount: metaTemplates.length,
        selectedTemplate: campaignData.whatsapp_template_name,
        campaignName: campaignData.name
      });
    }
    
    switch (step) {
      case 1:
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span>📝</span>
                <span>Información Básica</span>
              </CardTitle>
              <CardDescription>
                Define el nombre y configuración inicial de tu campaña
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Nombre de campaña */}
              <div>
                <Label htmlFor="campaignName">Nombre de la Campaña</Label>
                <Input
                  id="campaignName"
                  placeholder="Ej: Recordatorio de Facturas Enero 2025"
                  value={campaignData.name}
                  onChange={(e) =>
                    setCampaignData({ ...campaignData, name: e.target.value })
                  }
                  className="mt-2"
                />
              </div>

              {/* Seleccionar Plantilla de WhatsApp (Meta) - Solo plantillas aprobadas */}
              <div className="border border-green-200 bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <Label htmlFor="whatsappTemplate" className="flex items-center gap-2">
                  <span className="text-green-600">📱</span>
                  Seleccionar plantilla de WhatsApp
                  <span className="text-xs text-red-600 font-semibold">*Requerido</span>
                </Label>
                <p className="text-xs text-gray-500 mt-1 mb-2">
                  Solo se muestran plantillas aprobadas por Meta para envíos de WhatsApp.
                </p>
                
                {/* Advertencia si no hay plantillas */}
                {!loadingMetaTemplates && metaTemplates.length === 0 && (
                  <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-start gap-2">
                      <span className="text-yellow-600 text-lg">⚠️</span>
                      <div className="text-xs text-yellow-800">
                        <p className="font-medium mb-1">No hay plantillas aprobadas disponibles</p>
                        <p>Debes crear y aprobar al menos una plantilla de WhatsApp antes de crear una campaña.</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <select
                  id="whatsappTemplate"
                  aria-label="Seleccionar plantilla de WhatsApp"
                  title="Seleccionar plantilla de WhatsApp (Meta)"
                  className="mt-2 w-full rounded-md border border-green-300 bg-white px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  value={campaignData.whatsapp_template_name || ""}
                  onChange={(e) => {
                    const selectedName = e.target.value;
                    const selectedTemplate = metaTemplates.find(t => t.name === selectedName);
                    console.log('📱 Plantilla seleccionada:', selectedName, selectedTemplate);
                    setCampaignData({
                      ...campaignData,
                      whatsapp_template_name: selectedName,
                      whatsapp_template_language: selectedTemplate?.language || 'es_MX',
                      // También actualizar campos legacy para compatibilidad
                      templateType: selectedName,
                      template_name: selectedName,
                    } as any);
                  }}
                >
                  <option value="">
                    {loadingMetaTemplates
                      ? '⏳ Cargando plantillas aprobadas...'
                      : metaTemplates.length > 0
                        ? '-- Selecciona una plantilla --'
                        : '-- No hay plantillas aprobadas disponibles --'}
                  </option>
                  {metaTemplates.map((tpl) => (
                    <option key={`${tpl.name}-${tpl.language}`} value={tpl.name}>
                      {tpl.name} ({tpl.language})
                    </option>
                  ))}
                </select>
                {campaignData.whatsapp_template_name && (
                  <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                    <span>✅</span>
                    <span>Plantilla seleccionada: <strong>{campaignData.whatsapp_template_name}</strong></span>
                  </div>
                )}

                <div className="mt-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm text-gray-600">¿No encuentras la plantilla que necesitas?</div>
                      <div className="text-xs text-gray-500 hidden md:block">
                        Crea una nueva plantilla y espera la aprobación de Meta.
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <Button
                        variant="outline"
                        onClick={goToCreateTemplate}
                        className="w-full md:w-auto bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold px-4 py-3 rounded-lg shadow-lg hover:scale-105 transform transition duration-150 ease-out ring-2 ring-offset-2 ring-transparent hover:ring-green-300 focus:outline-none focus:ring-green-400"
                      >
                        <span className="mr-2 text-lg leading-none">➕</span>
                        <span>Crear nueva plantilla</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fecha y hora de envío - CORREGIDO - Solo mostrar si hay hojas conectadas */}
              {hasConnectedSheets && (
                <div>
                  <Label>Fecha y Hora de Envío</Label>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Fecha */}
                    <Input
                      type="date"
                      id="sendDate"
                      value={
                        campaignData.sendDate && campaignData.sendDate instanceof Date
                          ? format(campaignData.sendDate, "yyyy-MM-dd")
                          : (typeof campaignData.sendDate === 'string' ? campaignData.sendDate : "")
                      }
                      onChange={(e) => {
                        const value = e.target.value; // Formato: "YYYY-MM-DD"
                        if (value) {
                          // Parsear la fecha en zona horaria local (no UTC)
                          const [year, month, day] = value.split('-').map(Number);
                          const localDate = new Date(year, month - 1, day); // month es 0-indexed
                          setCampaignData({ ...campaignData, sendDate: localDate });
                        } else {
                          setCampaignData({ ...campaignData, sendDate: undefined });
                        }
                      }}
                      className="w-full"
                      required
                    />

                    {/* Hora */}
                    <Input
                      type="time"
                      id="sendTime"
                      value={campaignData.sendTime || ""}
                      onChange={(e) =>
                        setCampaignData({ ...campaignData, sendTime: e.target.value })
                      }
                      className="w-full"
                      required
                    />
                  </div>

                  <div className="mt-4 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-cyan-900 mb-2">
                          Información importante - Ley 2300 de 2023
                        </p>
                        <p className="text-xs text-cyan-800 mb-2">
                          Debido a la <strong>Ley 2300 de 2023</strong> (Ley "Dejen de Fregar Colombia"),
                          los horarios permitidos para el envío de mensajería o publicidad son:
                        </p>
                        <ul className="text-xs text-cyan-800 list-disc list-inside mb-2 space-y-1">
                          <li><strong>Lunes a viernes:</strong> 7:00 AM - 7:00 PM</li>
                          <li><strong>Sábados:</strong> 8:00 AM - 3:00 PM</li>
                          <li><strong>Domingos y festivos:</strong> Prohibido</li>
                        </ul>
                        <p className="text-xs text-cyan-700 italic">
                          No nos hacemos responsables por ningún tipo de inconveniente, multa o sanción
                          derivada del envío de mensajes fuera de los horarios permitidos por la ley.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Mostrar fecha + hora seleccionadas - CORREGIDO */}
                  {(campaignData.sendDate || campaignData.sendTime) && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      {(() => {
                        try {
                          let dateObj: Date | null = null;

                          if (campaignData.sendDate instanceof Date) {
                            dateObj = campaignData.sendDate;
                          } else if (typeof campaignData.sendDate === 'string' && campaignData.sendDate) {
                            const [year, month, day] = campaignData.sendDate.split('-').map(Number);
                            dateObj = new Date(year, month - 1, day);
                          }

                          if (!dateObj || isNaN(dateObj.getTime())) {
                            return campaignData.sendTime ? `Hora: ${formatTime12h(campaignData.sendTime)}` : '';
                          }

                          const formattedDate = format(dateObj, "EEEE d 'de' MMMM yyyy", { locale: es });
                          const formattedTime = campaignData.sendTime ? ` a las ${formatTime12h(campaignData.sendTime)}` : '';

                          return formattedDate + formattedTime;
                        } catch (error) {
                          console.error('Error formateando fecha:', error);
                          return campaignData.sendTime ? `Hora: ${formatTime12h(campaignData.sendTime)}` : '';
                        }
                      })()}
                    </p>
                  )}
                </div>
              )}

              {/* Botones de navegación */}
              <div className="flex justify-between">
                <Button variant="outline" onClick={onBack}>
                  ← Cancelar
                </Button>
                <Button
                  onClick={() => {
                    // Validar que se haya seleccionado una plantilla
                    if (!campaignData.whatsapp_template_name) {
                      toast.error('Por favor selecciona una plantilla de WhatsApp antes de continuar');
                      return;
                    }
                    console.log('✅ Avanzando al paso 2 con plantilla:', campaignData.whatsapp_template_name);
                    setStep(2);
                  }}
                  disabled={!campaignData.name.trim() || !campaignData.whatsapp_template_name}
                  className="bg-gradient-to-r from-blue-600 to-purple-600"
                >
                  Siguiente →
                </Button>
              </div>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <InlineSheetsSelector
            onSheetSelected={handleSheetSelected}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            selectedSheetId={selectedSheet || campaignData.google_sheet_id}
            campaignType={campaignData.campaign_type || 'Marketing'}
          />
        );

      case 3:
        console.log('📋 Paso 3 - TemplateColumnMapper props:', {
          template_id: (campaignData as any).template_id,
          whatsapp_template_name: campaignData.whatsapp_template_name,
          google_sheet_id: campaignData.google_sheet_id || selectedSheet,
          phone_column: (campaignData as any).phone_column
        });
        
        return (
          <TemplateColumnMapper
            template_id={(campaignData as any).template_id || ''}
            whatsapp_template_name={campaignData.whatsapp_template_name || ''}
            google_sheet_id={campaignData.google_sheet_id || selectedSheet || ''}
            google_sheet_tab_id={
              selectedSheetTabId ??
              (campaignData as any).google_sheet_tab_id ??
              null
            }
            google_sheet_tab_name={
              selectedSheetTab ||
              (campaignData as any).google_sheet_tab_name ||
              ''
            }
            phone_column={(campaignData as any).phone_column || ''}
            onMappingComplete={(mapping) => {
              setColumnMapping(mapping);
              setStep(4);
            }}
            onBack={() => setStep(2)}
            initialMapping={columnMapping}
          />
        );

      case 4:
        // función auxiliar: obtener fila de ejemplo
        const getSampleRowObject = (sheetDataLocal: CampaignSheetData | null) => {
          if (!sheetDataLocal || !sheetDataLocal.rows || sheetDataLocal.rows.length === 0) return null;
          const cols = sheetDataLocal.columns || [];
          for (let r = 0; r < sheetDataLocal.rows.length; r++) {
            const row = sheetDataLocal.rows[r];
            let mapped: Record<string, any> = {};
            if (Array.isArray(row)) {
              cols.forEach((c, i) => { mapped[c] = row[i] ?? ""; });
            } else if (typeof row === "object" && row !== null) {
              mapped = row;
            } else {
              continue;
            }
            const hasValue = cols.some((c) => {
              const v = mapped[c];
              return v !== undefined && v !== null && String(v).trim() !== "";
            });
            if (hasValue) return mapped;
          }
          const first = sheetDataLocal.rows[0];
          if (Array.isArray(first)) {
            const mapped: Record<string, any> = {};
            (sheetDataLocal.columns || []).forEach((c, i) => (mapped[c] = first[i] ?? ""));
            return mapped;
          }
          return first || null;
        };

        // función para renderizar template
        const renderTemplateText = (template: string, sheetDataLocal: CampaignSheetData | null) => {
          if (!template) return "Tu mensaje aparecerá aquí";
          if (!sheetDataLocal || !sheetDataLocal.columns || sheetDataLocal.columns.length === 0) return template;
          const sampleRow = getSampleRowObject(sheetDataLocal);
          if (!sampleRow) return template;

          const normalized: Record<string, string> = {};
          for (const col of sheetDataLocal.columns) {
            const rawVal = sampleRow[col] ?? sampleRow[col.trim()] ?? sampleRow[col.replace(/\s+/g, "_")] ?? "";
            const valStr = rawVal === null || rawVal === undefined ? "" : String(rawVal);
            normalized[col] = valStr;
            normalized[col.trim()] = valStr;
            normalized[col.toLowerCase()] = valStr;
            normalized[col.trim().toLowerCase()] = valStr;
            normalized[col.replace(/\s+/g, "_")] = valStr;
            normalized[col.replace(/\s+/g, "_").toLowerCase()] = valStr;
          }

          const replaced = template.replace(/{{\s*([^}]+?)\s*}}/g, (_, varNameRaw: string) => {
            const varName = varNameRaw.trim();
            const candidates = [
              sampleRow[varName],
              sampleRow[varName.trim()],
              normalized[varName],
              normalized[varName.trim()],
              normalized[varName.toLowerCase()],
              normalized[varName.replace(/\s+/g, "_")],
              normalized[varName.replace(/\s+/g, "_").toLowerCase()],
            ];
            const found = candidates.find((c) => c !== undefined && c !== null && String(c).trim() !== "");
            return found !== undefined ? String(found) : "";
          });

          return replaced;
        };

        // Preparar datos para el resumen - buscar en metaTemplates (templates de Meta WhatsApp)
        const selectedMetaTemplate = metaTemplates.find(t => t.name === campaignData.whatsapp_template_name);

        // Buscar también la plantilla en nuestra BD local para obtener header_image_url correcto
        const selectedDbTemplate = templates.find(t =>
          t.meta_template_name === campaignData.whatsapp_template_name ||
          t.name === campaignData.whatsapp_template_name
        );

        // Usamos PRIMERO selectedMetaTemplate?.body para EVITAR que el texto del template incluya los botones (que se guardaban como texto en versiones legacy)
        // Si no hay body de Meta, caemos en el mensaje raw de la base de datos
        const templateContent = selectedMetaTemplate?.body || campaignData.message_template || "";

        // ✅ Header image para el mockup: priorizar DB porque Meta API devuelve handles internos inválidos
        const templateHeaderImageUrl = selectedDbTemplate?.header_image_url || selectedMetaTemplate?.header_image_url || null;
        const templateHeaderType = selectedMetaTemplate?.header_type ?? (templateHeaderImageUrl ? 'IMAGE' : null);
        const templateName = campaignData.whatsapp_template_name || "Plantilla seleccionada";
        const sampleRow = getSampleRowObject(sheetData);

        const selectedSheetObj = sheets.find(s => (s.sheet_id === selectedSheet) || (s.id === selectedSheet));
        const selectedSheetName = selectedSheetObj ? (selectedSheetObj.sheet_name || selectedSheetObj.name || String(selectedSheet)) : (selectedSheet || '—');

        const scheduledAt = campaignData.sendDate
          ? `${format(campaignData.sendDate, "EEEE, d 'de' MMMM yyyy", { locale: es })}${campaignData.sendTime ? ` • ${formatTime12h(campaignData.sendTime)}` : ''}`
          : (campaignData.sendTime ? `Hora: ${formatTime12h(campaignData.sendTime)}` : '—');

        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span>👀</span>
                <span>Vista Previa</span>
              </CardTitle>
              <CardDescription>
                Revisa cómo se verán tus mensajes antes de crear la campaña.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4 md:px-6 items-start">
                {/* Resumen ejecutivo */}
                <div className="md:col-span-2 space-y-4">
                  <div className="bg-gradient-to-b from-white to-gray-50 border rounded-xl p-6 shadow-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-xs text-gray-400 uppercase tracking-wide">Nombre de la campaña</div>
                        <div className="mt-2 text-2xl font-semibold text-gray-900 leading-tight">
                          {campaignData.name || '—'}
                        </div>

                        <div className="mt-3">
                          <div className="text-xs text-gray-400 uppercase tracking-wide">Plantilla</div>
                          <div className="mt-1 text-sm font-medium text-gray-800">
                            {templateName}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end">
                        <div className="text-xs text-gray-400 uppercase tracking-wide">Fecha y hora de envío</div>
                        <div className="mt-2 text-sm font-medium text-gray-800 text-right">
                          {scheduledAt}
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                      <div className="rounded-md p-3 bg-white border">
                        <div className="text-xs text-gray-400">Hoja de datos</div>
                        <div className="mt-1 font-medium text-gray-800 truncate">{selectedSheetName}</div>
                      </div>

                      <div className="rounded-md p-3 bg-white border">
                        <div className="text-xs text-gray-400">Columna teléfono</div>
                        <div className="mt-1 font-medium text-gray-800">
                          {(campaignData as any).phone_column || 'Automático'}
                        </div>
                      </div>

                      <div className="rounded-md p-3 bg-white border">
                        <div className="text-xs text-gray-400">Envíos estimados</div>
                        <div className="mt-1 font-medium text-gray-800">
                          {sheetData?.rows?.length ?? 0} mensajes
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Columna izquierda */}
                <div className="space-y-4">
                  <div className="bg-gray-50 border rounded-lg p-4 space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm text-gray-500">Contenido de la plantilla</div>
                        <div className="text-xs text-gray-400">{templateName}</div>
                      </div>
                      <pre
                        className="whitespace-pre-wrap text-sm bg-white border rounded p-3 text-gray-800"
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        {templateContent || 'No hay contenido de plantilla disponible.'}
                      </pre>
                    </div>

                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm text-gray-500 font-medium">Configuración de envío de mensajes vía WhatsApp</div>
                        <div className="text-xs text-gray-400">Destinatarios</div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="inline-flex items-center rounded-md border bg-gray-50 px-3 py-2 text-sm text-gray-700 select-none">
                          {campaignData.country_code || '+57'}
                        </div>

                        <select
                          id="phoneColumn"
                          value={(campaignData as any).phone_column || ""}
                          onChange={(e) =>
                            setCampaignData({ ...campaignData, phone_column: e.target.value })
                          }
                          className="flex-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        >
                          <option value="">
                            {sheetData?.columns && sheetData.columns.length > 0
                              ? '-- Selecciona la columna --'
                              : '-- Selecciona una hoja primero --'}
                          </option>
                          {sheetData?.columns?.map((col) => (
                            <option key={col} value={col}>
                              {col}
                            </option>
                          ))}
                        </select>
                      </div>

                      <p className="mt-2 text-xs text-gray-500">
                        No te preocupes por el formato del número en tu hoja. Ya sea que esté escrito con o sin prefijo, el sistema lo formateará automáticamente y lo enviará correctamente con el código internacional correspondiente.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Columna derecha: vista previa WhatsApp */}
                <div className="flex flex-col items-center mb-4 md:sticky md:top-24 md:self-start">
                  <div className="flex justify-center w-full">
                    <div className="w-full max-w-[360px] mx-auto">
                      <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-gray-900 ring-1 ring-black/5" style={{ height: 600, backgroundColor: '#000' }}>
                        <div className="absolute top-0 w-full h-8 bg-transparent z-20 flex justify-between px-6 items-center pt-3">
                          <span className="text-[11px] text-white font-medium tracking-wide">
                            {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase()}
                          </span>
                          <div className="flex gap-1.5 items-center">
                            <div className="w-3 h-3 border-[1.5px] border-white rounded-full"></div>
                            <div className="w-4 h-2 border border-white rounded-[2px] relative flex p-[0.5px]">
                              <div className="bg-white h-full w-[90%] rounded-[0.5px]"></div>
                              <div className="absolute -right-[1.5px] top-[1.5px] h-[2px] w-[1px] bg-white rounded-r-[1px]"></div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between px-3 py-2 pt-10" style={{ background: "#075E54" }}>
                          <div className="flex items-center gap-3">
                            <img
                              src="https://i.pravatar.cc/120?img=32"
                              alt="avatar"
                              className="w-10 h-10 rounded-full object-cover border border-black/20"
                            />
                            <div className="leading-tight">
                              <div className="text-sm font-medium text-white/95">
                                {(() => {
                                  const country = (campaignData as any).country_code || defaultCountryCode;
                                  if (!sampleRow) return `${country} ··· ··· ····`;

                                  const col = (campaignData as any).phone_column;
                                  const raw =
                                    (col && sampleRow[col]) ||
                                    sampleRow['WhatsApp'] ||
                                    sampleRow['Telefono'] ||
                                    sampleRow['Telefono móvil'] ||
                                    sampleRow['Phone'] ||
                                    "";

                                  const rawStr = String(raw || "").trim();
                                  const digitsOnly = rawStr.replace(/\D/g, ""); // solo dígitos

                                  let localPart = "";
                                  if (digitsOnly.length === 12 && digitsOnly.startsWith("57")) {
                                    localPart = digitsOnly.slice(2);
                                  } else if (digitsOnly.length > 0) {
                                    localPart = digitsOnly;
                                  } else {
                                    localPart = rawStr || "";
                                  }

                                  return `${country} ${localPart || '··· ··· ····'}`;
                                })()}
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-white/80">Ahora</div>
                        </div>

                        <div className="bg-[#ECE5DD] p-3 flex flex-col h-full" style={{ paddingBottom: '20px' }}>
                          <div className="relative rounded-lg overflow-hidden flex-1" style={{ minHeight: 0 }}>
                            <div
                              className="absolute inset-0 pointer-events-none"
                              style={{
                                backgroundImage:
                                  "radial-gradient(circle at 10px 10px, rgba(0,0,0,0.02) 1px, transparent 2px), radial-gradient(circle at 30px 30px, rgba(0,0,0,0.01) 1px, transparent 2px)",
                                backgroundSize: "40px 40px, 40px 40px",
                                backgroundRepeat: "repeat, repeat",
                                opacity: 0.45,
                              }}
                            />

                            <div className="relative h-full overflow-auto p-2 flex flex-col gap-3">
                              <div className="flex-1" />

                              <div className="flex justify-end">
                                <div className="max-w-[84%]">
                                  <div className="bg-[#dcf8c6] text-sm text-black rounded-2xl shadow-sm break-words leading-relaxed max-w-full overflow-hidden">
                                    {/* ✅ Header image in mockup */}
                                    {templateHeaderImageUrl && templateHeaderType === 'IMAGE' && (
                                      <img
                                        src={templateHeaderImageUrl}
                                        alt="Header"
                                        className="w-full h-32 object-cover"
                                      />
                                    )}
                                    <div className="px-4 py-3">
                                      {(() => {
                                        if (!templateContent) return "Tu mensaje aparecerá aquí";
                                        const processed = renderTemplateText(templateContent, sheetData);
                                        if (!processed || processed.trim() === "") return "Tu mensaje aparecerá aquí";

                                        // Usar Twemoji para consistencia
                                        return (
                                          <div className="whitespace-pre-wrap [&_img]:inline [&_img]:w-5 [&_img]:h-5 [&_img]:align-text-bottom [&_img]:mx-[1px]">
                                            <Twemoji text={processed.replace(/\*([^*]+)\*/g, '$1').replace(/_([^_]+)_/g, '$1').replace(/~([^~]+)~/g, '$1')} svg />
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1 text-right">09:15</div>
                                  </div>
                                </div>
                              </div>

                              {/* RENDERIZAR BOTONES RECTANGULARES DE WHATSAPP */}
                              {selectedMetaTemplate?.buttons && selectedMetaTemplate.buttons.length > 0 && (
                                <div className="flex justify-end mt-1">
                                  <div className="max-w-[84%] w-full flex flex-col gap-1.5">
                                    {selectedMetaTemplate.buttons.map((btn, idx) => (
                                      <div key={idx} className="bg-white/95 text-[#00a884] text-[15px] font-medium rounded-lg shadow-sm py-2.5 px-4 text-center flex items-center justify-center gap-2 border border-gray-100">
                                        {btn.type === 'URL' && <span className="text-gray-400 text-lg">🔗</span>}
                                        {btn.type === 'PHONE_NUMBER' && <span className="text-gray-400 text-lg">📞</span>}
                                        {btn.type === 'QUICK_REPLY' && <span className="text-gray-400 text-lg">↩️</span>}
                                        <span className="truncate">{btn.text}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="mt-3 bg-white rounded-full px-3 py-2 flex items-center gap-3 shadow-[0_1px_0_rgba(0,0,0,0.06)] flex-none">
                              <div className="w-9 h-9 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-green-600 font-bold" aria-hidden="true">
                                +
                              </div>
                              <div className="flex items-center gap-2 flex-1">
                                <div className="flex-1 text-sm text-gray-600 select-none" aria-hidden="true">Escribe un mensaje...</div>
                              </div>
                              <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center text-white text-sm">✓</div>
                            </div>
                          </div>

                          <div className="h-3 bg-black/5" />
                        </div>

                        <div className="mt-4 text-center max-w-xs mx-auto">
                          <div className="text-sm font-semibold text-gray-900">Vista previa WhatsApp</div>
                          <div className="mt-1 text-xs text-gray-500">
                            Contenido de ejemplo — los datos mostrados provienen de una fila de muestra.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botones */}
                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep(2)}>← Volver a Seleccionar Datos</Button>
                  <Button
                    onClick={saveCampaign}
                    disabled={saving}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Guardando...
                      </>
                    ) : editingCampaign ? (
                      <>💾 Guardar cambios</>
                    ) : (
                      <>🚀 Crear campaña</>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
      {!editingCampaign && (
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Crear Nueva Campaña</h1>
          <p className="text-gray-600">Configura tu campaña de WhatsApp paso a paso</p>
        </div>
      )}

      {/* Plan Status Alert - Solo mostrar si está bloqueado */}
      {isBlocked && (
        <div className="mb-6">
          <PlanStatusAlert showOnlyBlocked={true} />
          <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
            <p className="text-sm text-gray-600 mb-3">
              No puedes crear nuevas campañas mientras tu cuenta esté bloqueada.
            </p>
            <button
              onClick={onBack}
              className="px-6 py-2.5 rounded-lg text-white font-medium shadow-md transition-all active:scale-95 bg-gradient-to-r from-[#1f4e66] to-[#1CB3B3] hover:opacity-90"
            >
              Volver atrás
            </button>
          </div>
        </div>
      )}

      {/* Solo mostrar el contenido si no está bloqueado */}
      {!isBlocked && (
        <>
          {/* Modal: Crear plantilla (simple, scroll vertical, sin sidebar) */}
          {showCreateTemplateModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setShowCreateTemplateModal(false)}
              />
              <div className="relative w-full max-w-4xl mx-4 z-10">
                {/* Contenedor minimal: sólo la funcionalidad interna del formulario (sin header/encuadre externo) */}
                <div className="overflow-auto" style={{ maxHeight: "85vh" }}>
                  <div className="p-4">
                    <CreateTemplateForm
                      newTemplate={newTemplate}
                      setNewTemplate={setNewTemplate}
                      extractVariables={(t: string) => {
                        try {
                          return extractVariablesFromTemplate(t).map((v) => v.name);
                        } catch {
                          const re = /\{\{\s*([^}]+?)\s*\}\}/g;
                          const vars: string[] = [];
                          let m: RegExpExecArray | null;
                          while ((m = re.exec(t)) !== null) vars.push(m[1].trim());
                          return vars;
                        }
                      }}
                      handleCreateTemplate={handleCreateTemplate}
                      setShowCreateForm={setShowCreateTemplateModal}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modal: Seleccionar plantilla (MessageTemplates) */}
          {showTemplatesModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={() => setShowTemplatesModal(false)} />
              <div className="relative w-full max-w-4xl z-10 p-4 bg-white rounded">
                <MessageTemplates mode="selection" onSelectTemplate={handleSelectTemplate} />
              </div>
            </div>
          )}

          {/* Step Indicator */}
          <StepIndicator />

          {/* Step Content */}
          {renderStep()}
        </>
      )}
    </div>
  );
};

export default CampaignBuilder;